import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { fetchNotifications } from '@/api/notifications/notificationsApi';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { NOTIFICATIONS_QUERY_KEY } from '@/constants/queryKeys';
import { isChatMessageNotificationType } from '@/components/notifications/notificationPresentation';
import { useAuth } from '@/hooks/useAuth';
import {
  useMarkNotificationReadMutation,
  useUnreadNotificationCountQuery,
} from '@/hooks/useNotifications';
import {
  resolveInAppNavigationPath,
  resolveNotificationCenterRoute,
} from '@/utils/notificationNavigation';
import { showNewNotificationToast, showNewNotificationsSummaryToast } from '@/utils/notifications';

const LATEST_NOTIFICATIONS_FETCH_SIZE = 10;
const MAXIMUM_INDIVIDUAL_TOASTS = 3;

function isChatRoute(pathname: string): boolean {
  if (pathname.startsWith(APPLICATION_ROUTES.workspaceChat) === true) {
    return true;
  }
  return pathname.startsWith(APPLICATION_ROUTES.clientPortalChat) === true;
}

export function useNewNotificationToasts(): void {
  const { t } = useTranslation('notifications');
  const { status, activeScopeCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const markReadMutation = useMarkNotificationReadMutation();
  const unreadCountQuery = useUnreadNotificationCountQuery();

  const previousUnreadCountRef = useRef<number | null>(null);
  const seenNotificationIdsRef = useRef<Set<string>>(new Set());
  const isCheckingForNewNotificationsRef = useRef(false);
  const hasPendingRecheckRef = useRef(false);
  const locationPathnameRef = useRef(location.pathname);

  useEffect(
    function trackCurrentPathname() {
      locationPathnameRef.current = location.pathname;
    },
    [location.pathname]
  );

  useEffect(
    function resetTrackingForNewSession() {
      previousUnreadCountRef.current = null;
      seenNotificationIdsRef.current = new Set();
      hasPendingRecheckRef.current = false;
    },
    [status, activeScopeCode]
  );

  useEffect(
    function toastOnUnreadIncrease() {
      if (status !== 'authenticated') {
        return;
      }
      const currentUnreadCount = unreadCountQuery.data;
      if (currentUnreadCount === undefined) {
        return;
      }

      function checkForNewNotifications(isBaseline: boolean) {
        isCheckingForNewNotificationsRef.current = true;

        queryClient
          .fetchQuery({
            queryKey: NOTIFICATIONS_QUERY_KEY,
            queryFn: function loadLatestNotifications() {
              return fetchNotifications(1, LATEST_NOTIFICATIONS_FETCH_SIZE);
            },
            staleTime: 0,
          })
          .then(function processLatestPage(latestPage) {
            const newNotifications = latestPage.items.filter(function isUnseenUnread(item) {
              if (item.isRead === true) {
                return false;
              }
              return seenNotificationIdsRef.current.has(item.id) === false;
            });

            newNotifications.forEach(function markAsSeen(item) {
              seenNotificationIdsRef.current.add(item.id);
            });

            if (isBaseline === true) {
              return;
            }

            const toastableNotifications = newNotifications.filter(function isToastable(item) {
              if (isChatMessageNotificationType(item.notificationType) === false) {
                return true;
              }
              return isChatRoute(locationPathnameRef.current) === false;
            });

            if (toastableNotifications.length === 0) {
              return;
            }

            if (toastableNotifications.length > MAXIMUM_INDIVIDUAL_TOASTS) {
              showNewNotificationsSummaryToast({
                message: t('toast.summaryMessage', { count: toastableNotifications.length }),
                actionLabel: t('bell.viewAll'),
                onAction: function openNotificationCenter() {
                  navigate(resolveNotificationCenterRoute(activeScopeCode));
                },
              });
              return;
            }

            toastableNotifications.forEach(function toastNotification(item) {
              showNewNotificationToast({
                title: item.title,
                body: item.body,
                actionLabel: t('toast.openAction'),
                onAction: function openNotification() {
                  markReadMutation.mutate(item.id);
                  const navigationPath = resolveInAppNavigationPath(item.actionUrl);
                  if (navigationPath !== null) {
                    navigate(navigationPath);
                  }
                },
              });
            });
          })
          .finally(function resetCheckingFlag() {
            isCheckingForNewNotificationsRef.current = false;
            if (hasPendingRecheckRef.current === true) {
              hasPendingRecheckRef.current = false;
              checkForNewNotifications(false);
            }
          });
      }

      if (isCheckingForNewNotificationsRef.current === true) {
        const lastKnownUnreadCount = previousUnreadCountRef.current;
        if (lastKnownUnreadCount === null || currentUnreadCount > lastKnownUnreadCount) {
          previousUnreadCountRef.current = currentUnreadCount;
          hasPendingRecheckRef.current = true;
        } else {
          previousUnreadCountRef.current = currentUnreadCount;
        }
        return;
      }

      const previousUnreadCount = previousUnreadCountRef.current;
      previousUnreadCountRef.current = currentUnreadCount;
      const isBaseline = previousUnreadCount === null;

      if (isBaseline === false && currentUnreadCount <= previousUnreadCount) {
        return;
      }

      checkForNewNotifications(isBaseline);
    },
    [status, unreadCountQuery.data, queryClient, navigate, activeScopeCode, t, markReadMutation]
  );
}
